import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { MeetingsGroupComponent } from '@app/library/panes/meetings-stack/meetings-group/meetings-group.component';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { ContentRouter } from '@app/models/content-router';

import { MediaFileSelectorModule } from '@core/components/media-file-selector/media-file-selector.module';
import { MeetingsCardModule } from '@core/components/meeting-card/meetings-card.module';
import { MeetingCardHalfModule } from '@core/components/meeting-card-half/meeting-card-half.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { AccountService, AuthService, BillingService, IFileType } from '@core/services';
import { WebsocketService } from '@core/websocket';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { IMeetingFormReferences, MeetingFormModule, MeetingReferencesService, SpinnersModule } from 'lingo2-forms';
import {
  CurrencyEnum,
  FilePurposeEnum,
  IBillingPlan,
  Meeting,
  MeetingTypeEnum,
  User,
  UserStatusEnum,
} from 'lingo2-models';

import { OnUiButtonModule } from 'onclass-ui';
import { of, Subject, tap } from 'rxjs';
import { filter, mergeAll, switchMap, takeUntil } from 'rxjs/operators';

interface GroupMeetings {
  title: string;
  list: Meeting[];
}

@Component({
  selector: 'app-meetings-stack',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,

    ModalModule,
    MeetingFormModule,
    MobileModalModule,
    MediaFileSelectorModule,
    ScrollingModule,
    MeetingCardHalfModule,
    MeetingsCardModule,
    OnUiButtonModule,
    MeetingsGroupComponent,
    IconComponent,
  ],
  templateUrl: './meetings-stack.component.html',
  styleUrls: ['./meetings-stack.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsStackComponent extends AbstractPane implements OnInit {
  private authService = inject(AuthService);
  private billingService = inject(BillingService);
  private websocket = inject(WebsocketService);
  private store = inject(Store);
  private meetingReferences = inject(MeetingReferencesService);

  public newMeeting: Meeting;
  public references: IMeetingFormReferences;
  public currentPlan: IBillingPlan;
  public recommendedPlan: IBillingPlan;
  public creditsAmount: number;
  public cover: IFileType;
  public meetingForm: boolean;
  public openGallery = false;
  public filePurpose = FilePurposeEnum.content;
  public isGuest: boolean;
  public isTeacher: boolean;

  public _me: User;

  private loadCreditsSubject = this.register(new Subject<boolean>());

  ngOnInit() {
    this.meetingReferences
      .prepareReferences()
      .pipe(
        filter((r) => !!r),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (references) => {
          this.references = references;
          this.currentPlan = references.billingSettings.plan.plan;
          this.recommendedPlan = references.recommendedPlan;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this._me = me;
        this.isGuest = this._me && this._me.status === UserStatusEnum.guest;
        this.isTeacher = AccountService.isAsIfTeacher(this._me);
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
    this.newMeeting = new Meeting();

    of(...[this.loadCredits$, this.watchWsBalanceUpdate$, this.watchWsReconnected$])
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.loadCredits();
  }

  public get groupsMeetings(): GroupMeetings[] {
    const webinar = this.meetings.filter((_m) => _m.type === MeetingTypeEnum.webinar);
    const stream = this.meetings.filter((_m) => _m.type === MeetingTypeEnum.stream);
    const group = this.meetings.filter((_m) => _m.type === MeetingTypeEnum.group);
    const single = this.meetings.filter((_m) => _m.type === MeetingTypeEnum.single);

    return [
      webinar.length ? { title: 'Вебинары', list: webinar } : null,
      stream.length ? { title: 'Стримы', list: stream } : null,
      group.length ? { title: 'Групповые занятия', list: group } : null,
      single.length ? { title: 'Индивидуальные занятия', list: single } : null,
    ].filter((_g) => _g !== null);
  }

  public trackByGroupMeetingsFn(index, item: GroupMeetings): string {
    return item.title;
  }

  public goScheduleClass() {
    if (this.isGuest) {
      this.authService.showAuthModal(() => this.onToggleMeetingForm());
    } else {
      this.onToggleMeetingForm();
    }
  }

  public onToggleMeetingForm() {
    this.meetingForm = !this.meetingForm;
    this.detectChanges();
  }

  public onBuyCredits(): void {
    this.onOpenBuyCreditsModal();
  }

  public onActivatePlan(): void {
    // this.onOpenActivatePlanModal();
    // this.onCloseBuyCreditsModal();
  }

  public onOpenBuyCreditsModal() {
    // this.openBuyCreditsModal = true;
  }

  /** Credits */

  private loadCredits() {
    this.loadCreditsSubject.next(true);
  }

  private get loadCredits$() {
    return this.loadCreditsSubject.pipe(
      switchMap(() => this.billingService.getAvailableAmount(CurrencyEnum.credits)),
      tap((amount: number) => {
        this.creditsAmount = amount;
        this.detectChanges();
      }),
    );
  }

  private get watchWsBalanceUpdate$() {
    return this.websocket.onBalanceUpdate$().pipe(tap(() => this.loadCredits()));
  }

  private get watchWsReconnected$() {
    return this.websocket.status$.pipe(
      filter((isOnline) => isOnline),
      tap(() => this.loadCredits()),
    );
  }

  /** On saved */
  public onMeetingSaved(meeting: Meeting): void {
    this.goToMeeting(meeting);
  }

  private goToMeeting(meeting: Meeting) {
    void this.router.navigate(ContentRouter.meetingRoute(meeting)).then();
  }

  /** Gallery */

  public onOpenGallery(): void {
    this.openGallery = true;
    this.detectChanges();
  }

  public closeGallery() {
    this.openGallery = false;
    this.detectChanges();
  }

  public setCover(cover: IFileType) {
    this.cover = cover;
    this.detectChanges();
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = true;
    this.detectChanges();
  }

  hasContent(): boolean {
    return !!this._pane;
  }
}
